import type React from 'react'
import type { PenAnchorPoint } from '@/lib/elements/types/pen-anchor-point'
import { NODE_ACCENT_COLOR } from '../constants/node-chrome'
import type { DragTarget } from '../types/node-edit-targets'

interface NodeAnchorMarkersProps {
  points: PenAnchorPoint[]
  /** Shape origin the (shape-local) points are offset by. */
  ox: number
  oy: number
  /** Half the marker's size (canvas units). */
  anchorHalf: number
  /** Base stroke width (canvas units). */
  sw: number
  /** Selection was built via shift+click (see `multiSelectActiveRef`). */
  multiSelectActive: boolean
  selectedIndices: Set<number>
  /** Pen-edit sub-mode: anchors are display-only. */
  isPenEditMode: boolean
  onElementDown: (e: React.PointerEvent, target: DragTarget) => void
  onElementMove: (e: React.PointerEvent) => void
  onElementUp: (e: React.PointerEvent) => void
}

/** Anchor points — smooth anchors are circles, corners are squares; draggable outside pen-edit
 *  mode. */
export function NodeAnchorMarkers({
  points,
  ox,
  oy,
  anchorHalf,
  sw,
  multiSelectActive,
  selectedIndices,
  isPenEditMode,
  onElementDown,
  onElementMove,
  onElementUp,
}: NodeAnchorMarkersProps) {
  return (
    <>
      {points.map((pt, i) => {
        const cx = pt.x + ox
        const cy = pt.y + oy
        // Show a hollow (white fill, accent border) style for anchors that are part of an
        // active shift-multi-selection so the user can see which nodes will move together —
        // including the first node clicked, before a second one is added.
        const isSelected = multiSelectActive && selectedIndices.has(i)
        const nodeFill = isSelected ? 'white' : NODE_ACCENT_COLOR
        const nodeStroke = isSelected ? NODE_ACCENT_COLOR : undefined
        const nodeStrokeWidth = isSelected ? sw * 1.5 : undefined
        const nodeInteractionProps = {
          style: {
            pointerEvents: isPenEditMode ? ('none' as const) : ('all' as const),
            cursor: 'move' as const,
          },
          onPointerDown: isPenEditMode
            ? undefined
            : (e: React.PointerEvent) => onElementDown(e, { type: 'anchor', index: i }),
          onPointerMove: isPenEditMode ? undefined : onElementMove,
          onPointerUp: isPenEditMode ? undefined : onElementUp,
        }

        if (pt.type === 'smooth') {
          return (
            <circle
              key={pt.id}
              cx={cx}
              cy={cy}
              r={anchorHalf}
              fill={nodeFill}
              stroke={nodeStroke}
              strokeWidth={nodeStrokeWidth}
              {...nodeInteractionProps}
            />
          )
        }

        return (
          <rect
            key={pt.id}
            x={cx - anchorHalf}
            y={cy - anchorHalf}
            width={anchorHalf * 2}
            height={anchorHalf * 2}
            fill={nodeFill}
            stroke={nodeStroke}
            strokeWidth={nodeStrokeWidth}
            {...nodeInteractionProps}
          />
        )
      })}
    </>
  )
}
